import { useState } from 'react';
import { Send, Trash2, Pencil, Check, X } from 'lucide-react';
import { frameOrderApi } from '../../services/frameOrderApi';
import type { FrameOrderComment } from '../../types/frameOrders';

interface FrameOrderCommentsProps {
    documentId: string;
    comments: FrameOrderComment[];
    canEdit: boolean;
    onCommentAdded: () => void;
}

function CommentItem({ comment, documentId, canEdit, onChanged }: {
    comment: FrameOrderComment;
    documentId: string;
    canEdit: boolean;
    onChanged: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(comment.CommentText);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!editText.trim() || saving) return;
        setSaving(true);
        try {
            await frameOrderApi.updateComment(documentId, comment.Id, editText.trim());
            setEditing(false);
            onChanged();
        } catch (err) {
            console.error('Failed to update comment:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await frameOrderApi.deleteComment(documentId, comment.Id);
            onChanged();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    const handleCancel = () => {
        setEditText(comment.CommentText);
        setEditing(false);
    };

    return (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
            {editing ? (
                <div>
                    <textarea
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        maxLength={2000}
                        rows={2}
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                    />
                    <div className="mt-1 flex justify-end gap-1">
                        <button
                            onClick={handleCancel}
                            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100"
                        >
                            <X className="h-3 w-3" /> Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!editText.trim() || saving}
                            className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Check className="h-3 w-3" /> {saving ? '...' : 'Save'}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.CommentText}</p>
                        {canEdit && (
                            <div className="flex shrink-0 gap-0.5">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                                    className="rounded p-0.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
                                    title="Edit comment"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                                    title="Delete comment"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                        {comment.CreatedBy} &middot; {new Date(comment.CreatedAt).toLocaleString('de-CH')}
                    </p>
                </>
            )}
        </div>
    );
}

export function FrameOrderComments({ documentId, comments, canEdit, onCommentAdded }: FrameOrderCommentsProps) {
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim() || submitting) return;

        setSubmitting(true);
        try {
            await frameOrderApi.addComment(documentId, newComment.trim());
            setNewComment('');
            onCommentAdded();
        } catch (err) {
            console.error('Failed to add comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-700">
                Comments ({comments.length})
            </h4>

            {comments.length === 0 && (
                <p className="py-4 text-center text-xs text-gray-400">No comments yet.</p>
            )}

            <div className="max-h-64 space-y-2 overflow-y-auto">
                {comments.map(comment => (
                    <CommentItem
                        key={comment.Id}
                        comment={comment}
                        documentId={documentId}
                        canEdit={canEdit}
                        onChanged={onCommentAdded}
                    />
                ))}
            </div>

            {canEdit && (
                <form onSubmit={handleSubmit} className="mt-3">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            maxLength={2000}
                            className="h-8 flex-1 rounded-md border border-gray-300 px-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            type="submit"
                            disabled={!newComment.trim() || submitting}
                            className="inline-flex h-8 items-center gap-1 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Send className="h-3 w-3" />
                            {submitting ? '...' : 'Send'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
